/**
 * Colonnes de la liste des campagnes (plan v3, phase 7).
 *
 * Écrites contre la projection de `listerCampagnesVue` : aucune lecture de
 * parcours côté serveur, les compteurs viennent de `progressionGlobale`.
 */
import {
  colonneApplications,
  colonneListeFiltrable,
  FACETTE_APPLICATIONS,
  FACETTE_TEMPLATE,
} from "@repo/campagne";
import type { ApplicationEnum } from "@repo/utils";
import {
  Badge,
  Button,
  Progress,
  StatutCampagneBadge,
  filtreFacette,
  filtreRecherche,
  triOrdonne,
  type ColonneDataTable,
  type FacetteFiltre,
} from "@repo/ui";
import { dateCourte } from "@repo/utils";
import { Mail, UserPlus, Users } from "lucide-react";
import type { Id } from "@convex/_generated/dataModel";

/** Ordre métier du statut : cycle de vie, pas alphabet. */
const ORDRE_STATUT = ["brouillon", "active", "terminee"] as const;

const LIBELLES_STATUT: Record<string, string> = {
  brouillon: "Brouillon",
  active: "Active",
  terminee: "Terminée",
};

/**
 * Valeurs de la colonne « Archivage » — même découpage que le portail admin :
 * une colonne dédiée plutôt qu'un 4ᵉ statut, une archivée restant « terminée ».
 */
export const ETAT_ARCHIVE = {
  ACTIVES: "actives",
  ARCHIVEES: "archivees",
} as const;

/** Facettes de la liste des campagnes (plan v3, phase 6). */
export const FACETTES_CAMPAGNES: FacetteFiltre[] = [
  {
    colonne: "statut",
    libelle: "Statut",
    libelles: LIBELLES_STATUT,
    ordre: ORDRE_STATUT,
  },
  FACETTE_APPLICATIONS,
  FACETTE_TEMPLATE,
  {
    colonne: "archive",
    libelle: "Archivage",
    libelles: {
      [ETAT_ARCHIVE.ACTIVES]: "Actives",
      [ETAT_ARCHIVE.ARCHIVEES]: "Archivées",
    },
    ordre: [ETAT_ARCHIVE.ACTIVES, ETAT_ARCHIVE.ARCHIVEES],
  },
];

/** Recherche de la liste des campagnes, à passer à `barreFiltres`. */
export const RECHERCHE_CAMPAGNES = {
  colonne: "nom",
  placeholder: "Nom de campagne…",
  libelle: "Rechercher une campagne",
} as const;

export type CampagneLigne = {
  _id: Id<"campagnes">;
  nom: string;
  applications: ApplicationEnum[];
  statut: string;
  dateCreation: number;
  anonyme?: boolean;
  archivedAt?: number;
  nbCandidats: number;
  progression: {
    total: number;
    terminees: number;
    enCours: number;
    nonCommencees: number;
    pourcentage: number;
  };
  templates: string[];
};

/** Coupe les mots composés sans casser la mise en page (`_` et `-`). */
function TemplatesCellule({ templates }: { templates: string[] }) {
  const texte = templates.join(", ");
  if (!texte) return <span className="text-muted-foreground">—</span>;
  return (
    <span className="text-sm">
      {texte.split(/([_-])/).map((part, i) =>
        part === "_" || part === "-" ? (
          <span key={i}>
            {part}
            <wbr />
          </span>
        ) : (
          part
        ),
      )}
    </span>
  );
}

export function colonnesCampagnes(actions: {
  onAjouterCandidats: (campagne: CampagneLigne) => void;
  onEnvoyerLiens: (campagne: CampagneLigne) => void;
}): ColonneDataTable<CampagneLigne>[] {
  return [
    {
      id: "nom",
      header: "Nom",
      accessorFn: (c) => c.nom,
      cell: ({ row }) => (
        <div className="flex items-center gap-2 min-w-0">
          <span
            className={`font-medium truncate ${row.original.archivedAt ? "text-muted-foreground" : ""}`}
          >
            {row.original.nom}
          </span>
          {row.original.archivedAt && (
            <Badge variant="outline" className="shrink-0 text-xs">
              Archivée
            </Badge>
          )}
        </div>
      ),
      enableHiding: false,
      filterFn: filtreRecherche<CampagneLigne>((c) => [c.nom]),
      meta: { libelle: "Nom", largeur: "28%" },
    },
    colonneApplications<CampagneLigne>((c) => c.applications, "12%"),
    {
      id: "templates",
      header: "Template",
      accessorFn: (c) => c.templates.join(", "),
      // Filtre « contient ce template », partagé avec le portail admin.
      ...colonneListeFiltrable<CampagneLigne>((c) => c.templates),
      cell: ({ row }) => <TemplatesCellule templates={row.original.templates} />,
      meta: { libelle: "Template", largeur: "13%" },
    },
    {
      id: "nbCandidats",
      header: "Candidats",
      accessorFn: (c) => c.nbCandidats,
      cell: ({ row }) => (
        <div className="flex items-center gap-1">
          <Users className="h-4 w-4 text-muted-foreground" />
          <span className="data">{row.original.nbCandidats}</span>
        </div>
      ),
      meta: { libelle: "Candidats", largeur: "10%" },
    },
    {
      id: "progression",
      header: "Progression",
      accessorFn: (c) => c.progression.pourcentage,
      cell: ({ row }) => {
        const p = row.original.progression;
        return (
          <div>
            <div className="flex items-center justify-between text-sm mb-1">
              <span className="data">{p.pourcentage}%</span>
              <span className="data text-muted-foreground">
                {p.terminees}/{p.total}
              </span>
            </div>
            <Progress value={p.pourcentage} className="h-2" />
          </div>
        );
      },
      meta: { libelle: "Progression", largeur: "15%" },
    },
    {
      id: "statut",
      header: "Statut",
      accessorFn: (c) => c.statut,
      // Alimente les segments de statut et leurs compteurs (facettes).
      filterFn: filtreFacette<CampagneLigne>(),
      sortFn: triOrdonne<CampagneLigne>(ORDRE_STATUT),
      cell: ({ row }) => <StatutCampagneBadge statut={row.original.statut} />,
      meta: { libelle: "Statut", largeur: "10%" },
    },
    {
      id: "dateCreation",
      header: "Créée le",
      accessorFn: (c) => c.dateCreation,
      cell: ({ row }) => (
        <span className="data">{dateCourte(row.original.dateCreation)}</span>
      ),
      meta: { libelle: "Date de création", largeur: "10%" },
    },
    {
      // Colonne porteuse de la facette « Archivage ». Masquée par défaut :
      // l'état se lit sur le badge de la colonne Nom.
      id: "archive",
      header: "Archivage",
      accessorFn: (c) =>
        c.archivedAt ? ETAT_ARCHIVE.ARCHIVEES : ETAT_ARCHIVE.ACTIVES,
      filterFn: filtreFacette<CampagneLigne>(),
      cell: ({ row }) => (
        <span className="text-sm text-muted-foreground">
          {row.original.archivedAt ? dateCourte(row.original.archivedAt) : "—"}
        </span>
      ),
      meta: { libelle: "Archivage", largeur: "10%", masqueeParDefaut: true },
    },
    {
      id: "actions",
      header: "",
      enableSorting: false,
      enableHiding: false,
      // Archivée : consultation seule, aucune action d'écriture proposée.
      cell: ({ row }) => row.original.archivedAt ? null : (
        <div className="flex items-center justify-end gap-1">
          <Button
            variant="ghost"
            size="icon"
            aria-label={`Ajouter des candidats à ${row.original.nom}`}
            title="Ajouter des candidats"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              actions.onAjouterCandidats(row.original);
            }}
          >
            <UserPlus className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            disabled={row.original.anonyme}
            aria-label={`Envoyer les liens de ${row.original.nom}`}
            title="Envoyer les liens par email"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              actions.onEnvoyerLiens(row.original);
            }}
          >
            <Mail className="h-4 w-4" />
          </Button>
        </div>
      ),
      meta: { largeur: "12%", aDroite: true },
    },
  ];
}
